// app/src/dashboard/widgets/chart/ChartToolbar.tsx
import { useState, useRef, useEffect } from "react";
import { Crosshair, ChevronDown, Lock, LockOpen } from "lucide-react";
import type { Timeframe } from "./chartTypes";
import useTranslation from "../../../client/hooks/useTranslation";

const TIMEFRAMES: Timeframe[] = ["1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w"];

interface SymbolEntry { symbol: string; label: string; yahooSymbol: string; }
interface SymbolCategory { category: string; symbols: SymbolEntry[]; }

const PRESET_CATEGORIES: SymbolCategory[] = [
  {
    category: "Indices",
    symbols: [
      { symbol: "NQ", label: "Nasdaq 100", yahooSymbol: "NQ=F" },
      { symbol: "MNQ", label: "Micro Nasdaq", yahooSymbol: "MNQ=F" },
      { symbol: "ES", label: "S&P 500", yahooSymbol: "ES=F" },
      { symbol: "MES", label: "Micro S&P", yahooSymbol: "MES=F" },
      { symbol: "YM", label: "Dow Jones", yahooSymbol: "YM=F" },
      { symbol: "RTY", label: "Russell 2000", yahooSymbol: "RTY=F" },
      { symbol: "CAC", label: "CAC 40", yahooSymbol: "^FCHI" },
      { symbol: "DAX", label: "DAX", yahooSymbol: "^GDAXI" },
    ],
  },
  {
    category: "Forex",
    symbols: [
      { symbol: "EURUSD", label: "EUR/USD", yahooSymbol: "EURUSD=X" },
      { symbol: "GBPUSD", label: "GBP/USD", yahooSymbol: "GBPUSD=X" },
      { symbol: "USDJPY", label: "USD/JPY", yahooSymbol: "USDJPY=X" },
      { symbol: "AUDUSD", label: "AUD/USD", yahooSymbol: "AUDUSD=X" },
      { symbol: "USDCAD", label: "USD/CAD", yahooSymbol: "USDCAD=X" },
      { symbol: "USDCHF", label: "USD/CHF", yahooSymbol: "USDCHF=X" },
      { symbol: "NZDUSD", label: "NZD/USD", yahooSymbol: "NZDUSD=X" },
      { symbol: "EURGBP", label: "EUR/GBP", yahooSymbol: "EURGBP=X" },
    ],
  },
  {
    category: "Crypto",
    symbols: [
      { symbol: "BTC", label: "Bitcoin", yahooSymbol: "BTC-USD" },
      { symbol: "ETH", label: "Ethereum", yahooSymbol: "ETH-USD" },
      { symbol: "SOL", label: "Solana", yahooSymbol: "SOL-USD" },
      { symbol: "XRP", label: "XRP", yahooSymbol: "XRP-USD" },
      { symbol: "BNB", label: "BNB", yahooSymbol: "BNB-USD" },
    ],
  },
  {
    category: "Matières premières",
    symbols: [
      { symbol: "GOLD", label: "Or", yahooSymbol: "GC=F" },
      { symbol: "SILVER", label: "Argent", yahooSymbol: "SI=F" },
      { symbol: "OIL", label: "Pétrole WTI", yahooSymbol: "CL=F" },
      { symbol: "NG", label: "Gaz naturel", yahooSymbol: "NG=F" },
    ],
  },
  {
    category: "Actions",
    symbols: [
      { symbol: "AAPL", label: "Apple", yahooSymbol: "AAPL" },
      { symbol: "MSFT", label: "Microsoft", yahooSymbol: "MSFT" },
      { symbol: "NVDA", label: "Nvidia", yahooSymbol: "NVDA" },
      { symbol: "TSLA", label: "Tesla", yahooSymbol: "TSLA" },
      { symbol: "AMZN", label: "Amazon", yahooSymbol: "AMZN" },
      { symbol: "GOOGL", label: "Alphabet", yahooSymbol: "GOOGL" },
      { symbol: "META", label: "Meta", yahooSymbol: "META" },
    ],
  },
];

function getYahooSymbol(input: string): string {
  const upper = input.toUpperCase();
  for (const cat of PRESET_CATEGORIES) {
    for (const s of cat.symbols) {
      if (s.symbol === upper || s.yahooSymbol.toUpperCase() === upper) {
        return s.yahooSymbol;
      }
    }
  }
  return input;
}

function getDisplaySymbol(yahooSymbol: string): string {
  for (const cat of PRESET_CATEGORIES) {
    for (const s of cat.symbols) {
      if (s.yahooSymbol === yahooSymbol) return s.symbol;
    }
  }
  return yahooSymbol;
}

interface ChartToolbarProps {
  symbol: string;
  timeframe: Timeframe;
  showTrades: boolean;
  locked: boolean;
  hasMatchingTrades: boolean;
  tradeSymbols: string[];
  onSymbolChange: (yahooSymbol: string) => void;
  onTimeframeChange: (tf: Timeframe) => void;
  onToggleTrades: () => void;
  onToggleLock: () => void;
}

export function ChartToolbar({
  symbol,
  timeframe,
  showTrades,
  locked,
  hasMatchingTrades,
  tradeSymbols,
  onSymbolChange,
  onTimeframeChange,
  onToggleTrades,
  onToggleLock,
}: ChartToolbarProps) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayLabel = getDisplaySymbol(symbol);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Build "Mes trades" category from user's actual trade symbols
  const myTradeEntries: SymbolEntry[] = Array.from(new Set(tradeSymbols))
    .slice(0, 8)
    .map((s) => ({ symbol: getDisplaySymbol(s), label: s, yahooSymbol: s }));

  const allCategories: SymbolCategory[] = [
    ...(myTradeEntries.length > 0 ? [{ category: "Mes trades", symbols: myTradeEntries }] : []),
    ...PRESET_CATEGORIES,
  ];

  const visibleCategories = query.trim()
    ? allCategories
        .map((cat) => ({
          ...cat,
          symbols: cat.symbols.filter(
            (s) =>
              s.symbol.toLowerCase().includes(query.toLowerCase()) ||
              s.label.toLowerCase().includes(query.toLowerCase())
          ),
        }))
        .filter((cat) => cat.symbols.length > 0)
    : allCategories;

  const currentCategory = activeCategory ?? visibleCategories[0]?.category ?? null;
  const rightSymbols = visibleCategories.find((c) => c.category === currentCategory)?.symbols ?? [];

  const handleSelect = (entry: SymbolEntry) => {
    onSymbolChange(entry.yahooSymbol);
    setOpen(false);
    setQuery("");
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") { setOpen(false); setQuery(""); }
    if (e.key === "Enter" && query.trim()) {
      onSymbolChange(getYahooSymbol(query.trim()));
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border shrink-0 flex-wrap">
      {/* Symbol picker */}
      <div ref={containerRef} className="relative">
        <button
          onClick={() => { setOpen((v) => !v); setTimeout(() => inputRef.current?.focus(), 50); }}
          className="flex items-center gap-1 text-xs font-mono bg-accent/40 border border-border rounded px-1.5 py-0.5 hover:border-primary transition-colors min-w-[72px]"
        >
          <span className="flex-1 text-left">{displayLabel || t.chart.symbol}</span>
          <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 z-50 rounded-lg border border-border bg-card shadow-xl" style={{ width: 320 }}>
            {/* Search input */}
            <div className="px-2 pt-2 pb-1 border-b border-border">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActiveCategory(null); }}
                onKeyDown={handleInputKeyDown}
                placeholder="Rechercher ou saisir un symbole…"
                className="w-full text-xs bg-accent/40 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* Two-column layout */}
            <div className="flex" style={{ height: 220 }}>
              {/* Col 1: categories */}
              <div className="w-32 border-r border-border overflow-y-auto py-1 shrink-0">
                {visibleCategories.map((cat) => (
                  <button
                    key={cat.category}
                    onMouseEnter={() => setActiveCategory(cat.category)}
                    onClick={() => setActiveCategory(cat.category)}
                    className={`w-full text-left px-2.5 py-1.5 text-[11px] transition-colors truncate ${
                      currentCategory === cat.category
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                    }`}
                  >
                    {cat.category}
                  </button>
                ))}
              </div>

              {/* Col 2: symbols */}
              <div className="flex-1 overflow-y-auto py-1">
                {rightSymbols.map((entry) => (
                  <button
                    key={entry.yahooSymbol}
                    onClick={() => handleSelect(entry)}
                    className={`w-full text-left px-2.5 py-1.5 flex items-center justify-between hover:bg-accent/40 transition-colors ${
                      entry.yahooSymbol === symbol ? "text-primary" : ""
                    }`}
                  >
                    <span className="text-[11px] font-mono font-medium">{entry.symbol}</span>
                    <span className="text-[10px] text-muted-foreground truncate ml-2">{entry.label}</span>
                  </button>
                ))}
                {rightSymbols.length === 0 && (
                  <p className="text-[11px] text-muted-foreground px-2.5 py-2">Aucun résultat</p>
                )}
              </div>
            </div>

            {query.trim() && (
              <div className="border-t border-border px-2.5 py-1.5">
                <button
                  onClick={() => { onSymbolChange(getYahooSymbol(query.trim())); setOpen(false); setQuery(""); }}
                  className="text-[11px] text-primary hover:underline"
                >
                  Utiliser "{query.trim().toUpperCase()}" directement
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Timeframe pills */}
      <div className="flex gap-0.5">
        {TIMEFRAMES.map((tf) => (
          <button
            key={tf}
            onClick={() => onTimeframeChange(tf)}
            className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
              tf === timeframe
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
            }`}
          >
            {tf}
          </button>
        ))}
      </div>

      <div className="ml-auto flex items-center gap-1">
        {/* Lock button */}
        <button
          onClick={onToggleLock}
          title={locked ? "Déverrouiller les réglages" : "Verrouiller les réglages"}
          className={`flex items-center justify-center w-6 h-6 rounded transition-colors ${
            locked
              ? "bg-primary/20 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
          }`}
        >
          {locked ? <Lock className="w-3 h-3" /> : <LockOpen className="w-3 h-3" />}
        </button>

        {/* Trade overlay toggle */}
        <button
          onClick={onToggleTrades}
          disabled={!hasMatchingTrades && !showTrades}
          title={!hasMatchingTrades ? t.chart.noTradesForSymbol : t.chart.showTrades}
          className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${
            showTrades && hasMatchingTrades
              ? "bg-primary/20 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/60 disabled:opacity-40 disabled:cursor-not-allowed"
          }`}
        >
          <Crosshair className="w-3 h-3" />
          <span className="hidden sm:inline">{t.chart.showTrades}</span>
        </button>
      </div>
    </div>
  );
}
